import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Avatar, Chip,
  LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem
} from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as TimeIcon,
  Assignment as TaskIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

const TeamWorkload = ({ tasks, employees }) => {
  const { translate } = useLanguage();
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: '',
    department: '',
    hourlyRate: 75,
    maxHoursPerWeek: 40
  });

  // Calculate workload for each employee
  const calculateWorkload = (employeeName) => {
    const employeeTasks = tasks.filter(task => task.assignee === employeeName);
    
    const totalEstimatedHours = employeeTasks.reduce((sum, task) => sum + task.estimatedHours, 0);
    const totalActualHours = employeeTasks.reduce((sum, task) => sum + task.actualHours, 0);
    const completedTasks = employeeTasks.filter(task => task.status === 'completed').length;
    const inProgressTasks = employeeTasks.filter(task => task.status === 'in_progress').length;
    const pendingTasks = employeeTasks.filter(task => task.status === 'pending').length;
    const overdueTasks = employeeTasks.filter(task => {
      const dueDate = new Date(task.dueDate);
      return dueDate < new Date() && task.status !== 'completed';
    }).length;

    // Calculate workload percentage (assuming 40 hours per week)
    const weeklyCapacity = 40;
    const workloadPercentage = (totalEstimatedHours / weeklyCapacity) * 100;

    return {
      totalTasks: employeeTasks.length,
      totalEstimatedHours,
      totalActualHours,
      completedTasks,
      inProgressTasks,
      pendingTasks,
      overdueTasks,
      workloadPercentage,
      efficiency: totalEstimatedHours > 0 ? (totalActualHours / totalEstimatedHours) * 100 : 0
    };
  };

  const handleAddEmployee = () => {
    if (newEmployee.name && newEmployee.role) {
      // TODO: Add employee to backend
      console.log('Adding employee:', newEmployee);
      setNewEmployee({
        name: '',
        role: '',
        department: '',
        hourlyRate: 75,
        maxHoursPerWeek: 40
      });
      setShowAddEmployee(false);
    }
  };

  const getWorkloadColor = (percentage) => {
    if (percentage >= 100) return 'error';
    if (percentage >= 80) return 'warning';
    return 'success';
  };

  const getEfficiencyColor = (percentage) => {
    if (percentage >= 100) return 'success';
    if (percentage >= 80) return 'warning';
    return 'error';
  };

  const departments = ['Engineering', 'Design', 'Management', 'IT', 'Operations', 'Sales', 'Marketing'];

  const roles = [
    'Frontend Developer', 'Backend Developer', 'Full Stack Developer',
    'UI/UX Designer', 'Project Manager', 'System Administrator',
    'DevOps Engineer', 'QA Engineer', 'Product Manager'
  ];

  return (
    <Box>
      {/* Team Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon color="primary" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{employees.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Team Members</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TaskIcon color="success" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{tasks.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Tasks</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TimeIcon color="info" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">
                    {tasks.reduce((sum, t) => sum + t.estimatedHours, 0)}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Hours</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon color="warning" />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">
                    {Math.round(tasks.reduce((sum, t) => sum + t.estimatedHours, 0) / employees.length)}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Avg per Person</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Team Members Workload */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Team Workload</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={() => setShowAddEmployee(true)}
            >
              Add Employee
            </Button>
          </Box>

          <Grid container spacing={3}>
            {employees.map((employee) => {
              const workload = calculateWorkload(employee.name);
              
              return (
                <Grid item xs={12} md={6} lg={4} key={employee.id}>
                  <Card variant="outlined">
                    <CardContent>
                      {/* Employee Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ mr: 2 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6">{employee.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {employee.role} • {employee.department}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Workload Progress */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Workload</Typography>
                          <Typography variant="body2">
                            {workload.workloadPercentage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(workload.workloadPercentage, 100)}
                          color={getWorkloadColor(workload.workloadPercentage)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      {/* Efficiency Progress */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Efficiency</Typography>
                          <Typography variant="body2">
                            {workload.efficiency.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(workload.efficiency, 100)}
                          color={getEfficiencyColor(workload.efficiency)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>

                      {/* Task Statistics */}
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="primary">
                              {workload.totalTasks}
                            </Typography>
                            <Typography variant="caption">Total Tasks</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" color="success.main">
                              {workload.completedTasks}
                            </Typography>
                            <Typography variant="caption">Completed</Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Task Status Chips */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {workload.inProgressTasks > 0 && (
                          <Chip
                            label={`${workload.inProgressTasks} In Progress`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {workload.pendingTasks > 0 && (
                          <Chip
                            label={`${workload.pendingTasks} Pending`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {workload.overdueTasks > 0 && (
                          <Chip
                            label={`${workload.overdueTasks} Overdue`}
                            size="small"
                            color="error"
                          />
                        )}
                      </Box>

                      {/* Hours Summary */}
                      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">
                          <TimeIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                          {workload.totalEstimatedHours}h estimated • {workload.totalActualHours}h logged
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Detailed Workload Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Workload Analysis
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="center">Tasks</TableCell>
                  <TableCell align="center">Workload</TableCell>
                  <TableCell align="center">Efficiency</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Hours</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => {
                  const workload = calculateWorkload(employee.name);
                  
                  return (
                    <TableRow key={employee.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {employee.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee.department}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{employee.role}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body1" fontWeight="medium">
                            {workload.totalTasks}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {workload.completedTasks} completed
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(workload.workloadPercentage, 100)}
                            color={getWorkloadColor(workload.workloadPercentage)}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">
                            {workload.workloadPercentage.toFixed(0)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(workload.efficiency, 100)}
                            color={getEfficiencyColor(workload.efficiency)}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">
                            {workload.efficiency.toFixed(0)}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {workload.overdueTasks > 0 ? (
                          <Chip
                            icon={<WarningIcon />}
                            label="Overdue"
                            color="error"
                            size="small"
                          />
                        ) : workload.workloadPercentage > 100 ? (
                          <Chip
                            icon={<WarningIcon />}
                            label="Overloaded"
                            color="warning"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="On Track"
                            color="success"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {workload.totalActualHours}h / {workload.totalEstimatedHours}h
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Employee Dialog */}
      <Dialog
        open={showAddEmployee}
        onClose={() => setShowAddEmployee(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Employee</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={newEmployee.name}
              onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={newEmployee.role}
                onChange={(e) => setNewEmployee({ ...newEmployee, role: e.target.value })}
                label="Role"
              >
                {roles.map(role => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={newEmployee.department}
                onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                label="Department"
              >
                {departments.map(dept => (
                  <MenuItem key={dept} value={dept}>
                    {dept}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Hourly Rate ($)"
              type="number"
              value={newEmployee.hourlyRate}
              onChange={(e) => setNewEmployee({ ...newEmployee, hourlyRate: parseFloat(e.target.value) || 0 })}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 0.01 }}
            />

            <TextField
              fullWidth
              label="Max Hours per Week"
              type="number"
              value={newEmployee.maxHoursPerWeek}
              onChange={(e) => setNewEmployee({ ...newEmployee, maxHoursPerWeek: parseInt(e.target.value) || 40 })}
              inputProps={{ min: 1, max: 80 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddEmployee(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddEmployee} variant="contained">
            Add Employee
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamWorkload; 